import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Product from '@models/product';
import { ProductModelProps } from '@typings/product';
import axios, { AxiosRequestConfig } from 'axios';
import React from 'react';
import useSWRInfinite from 'swr/infinite';

interface Key extends AxiosRequestConfig {
  currency: string;
  locale: string;
  filters?: Array<any>;
  sort?: any;
  options: { facets: Array<string> };
  page: number;
}

export const fetcher = async ({
  currency,
  locale,
  filters,
  page,
  sort,
  options,
  ...config
}: Key) => {
  const res = await axios({
    ...config,
    params: {
      filters: JSON.stringify(filters),
      page,
      sort: JSON.stringify(sort),
      options: JSON.stringify(options),
      currency,
      lang: locale,
    },
  });
  return {
    hits: res?.data?.hits?.map((hit: ProductModelProps) => new Product(hit)),
    nbPages: res?.data?.nbPages,
    page: res?.data?.page,
  };
};

interface Props {
  filters?: Array<any>;
  sort?: { field: string; direction: string };
  options?: { facets: Array<string> };
  _data?: {
    hits: Array<ProductModelProps>;
    nbPages: number;
    page: number;
  };
}

export const useProductGridData = (
  { filters, sort, options, _data }: Props,
  hasProducts = false,
) => {
  const { currency, locale } = useCurrentUrl();
  const swr = useSWRInfinite(
    (page) => {
      if (hasProducts) return null;
      return {
        url: '/api/product-data/searchProducts',
        method: 'GET',
        page,
        filters,
        sort,
        options,
        currency,
        locale,
      };
    },
    fetcher,
    {
      fallbackData: [
        {
          hits: _data?.hits?.map((hit: ProductModelProps) => new Product(hit)),
          nbPages: _data?.nbPages ?? 1,
          page: _data?.page ?? 0,
        },
      ],
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      parallel: true,
    },
  );

  return React.useMemo(() => {
    const { nbPages, page } = swr.data?.[swr.data.length - 1] ?? {};
    return {
      ...swr,
      data: {
        hits: swr.data?.map(({ hits }) => hits).flat(),
        nbPages,
        page,
      },
    };
  }, [swr]);
};
